// Breadcrumbs (DESIGN §3.2.3): `label` `fg-secondary`, Trenner „/“ in `fg-decor`, letztes Element
// `fg` ohne Link. Ab 4 Ebenen werden die mittleren in ein „…“-Menü gekürzt; mobil nur das
// vorletzte Element als Zurück-Link.
import { ArrowLeft } from '@phosphor-icons/react';
import { DropdownMenu } from 'radix-ui';
import { Fragment } from 'react';
import { cn } from '../kern/cn';
import { RouterLink } from '../kern/Link';

export interface Crumb {
  label: string;
  href?: string;
}

/** Sichtbare Einträge und die in „…“ gekürzten (ab 4 Ebenen: erste + „…“ + letzte zwei). */
export function collapseCrumbs(items: readonly Crumb[]): {
  visible: (Crumb | 'ellipsis')[];
  hidden: Crumb[];
} {
  if (items.length < 4) return { visible: [...items], hidden: [] };
  return {
    visible: [items[0]!, 'ellipsis', ...items.slice(-2)],
    hidden: items.slice(1, -2),
  };
}

export function Breadcrumbs({ items, className }: { items: readonly Crumb[]; className?: string }) {
  const { visible, hidden } = collapseCrumbs(items);
  const parent = items.length > 1 ? items[items.length - 2] : undefined;
  const last = items.at(-1);
  return (
    <nav aria-label="Breadcrumb" className={cn('min-w-0', className)}>
      {parent?.href ? (
        <RouterLink
          href={parent.href}
          className="inline-flex h-10 max-w-full items-center gap-2 rounded-full pr-2 text-button text-fg md:hidden"
        >
          <ArrowLeft size={20} aria-hidden className="shrink-0" />
          <span className="truncate">{parent.label}</span>
        </RouterLink>
      ) : (
        <span className="truncate text-label-strong text-fg md:hidden">{last?.label}</span>
      )}
      <ol className="hidden min-w-0 items-center gap-2 text-label md:flex">
        {visible.map((crumb, index) => {
          const isLast = index === visible.length - 1;
          return (
            <Fragment key={crumb === 'ellipsis' ? 'ellipsis' : `${index}-${crumb.label}`}>
              {index > 0 ? (
                <li aria-hidden className="text-fg-decor">
                  /
                </li>
              ) : null}
              <li className={cn('min-w-0', isLast ? 'flex-1' : 'shrink-0')}>
                {crumb === 'ellipsis' ? (
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger
                      aria-label="Show hidden levels"
                      className="rounded-xs px-1 text-fg-secondary hover:text-fg"
                    >
                      …
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        sideOffset={6}
                        className="z-(--bar-z-popover) min-w-48 rounded-xl bg-overlay p-2 shadow-2 data-[state=open]:animate-pop-in"
                      >
                        {hidden.map((h) => (
                          <DropdownMenu.Item
                            key={h.label}
                            asChild
                            className="flex h-10 items-center rounded-lg px-3 text-button text-fg outline-none data-highlighted:bg-card-hover"
                          >
                            <RouterLink href={h.href ?? '#'}>{h.label}</RouterLink>
                          </DropdownMenu.Item>
                        ))}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                ) : isLast || !crumb.href ? (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className={cn(
                      'block truncate',
                      isLast ? 'text-button text-fg' : 'text-fg-secondary',
                    )}
                  >
                    {crumb.label}
                  </span>
                ) : (
                  <RouterLink
                    href={crumb.href}
                    className="block truncate rounded-xs text-fg-secondary hover:text-fg hover:underline"
                  >
                    {crumb.label}
                  </RouterLink>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
